/**
 * Popup opt-in, parcours 3 écrans (type Aloe Paris).
 *   Écran 1 (email) et 2 (téléphone) : client-side, transitions JS, AUCUNE soumission.
 *   Écran 3 (le code) : rendu serveur au retour de la soumission native unique.
 *
 * Ce fichier ne doit JAMAIS intercepter la SOUMISSION du formulaire (pas de preventDefault sur
 * `submit`, pas de fetch). La navigation entre les écrans 1 et 2, elle, n'est PAS une soumission :
 * elle a le droit d'intercepter le clic du bouton « suivant » et la touche Entrée.
 * Mesuré le 2026-07-15 : un fetch vers /contact renvoie 400 « Missing CAPTCHA token » (le champ
 * h-captcha-response est vide au binding, rempli seulement au submit) et seule une soumission
 * native du thème déclenche le welcome. La soumission reste donc native pleine page.
 *
 * LE DÉFI hCAPTCHA ET LE TOP LAYER (mesuré le 2026-07-16 sur mondaydrink.co, ne pas défaire) :
 * hCaptcha monte son iframe de défi en enfant direct de <body>, hors du dialog. Un <dialog> en
 * showModal() rend inerte tout le reste du document. Tant que le popup est modal, le défi est
 * visible mais inatteignable et le clic dessus ferme le popup. Parade : sur `submit`, SANS
 * preventDefault, on SURVEILLE l'apparition d'un défi visible et on ne ferme le dialog QUE s'il
 * apparaît (il quitte alors le top layer, le défi redevient cliquable). Dans le cas courant (pas
 * de défi), on NE ferme PAS : la page navigue en ~1s et le popup reste visible jusqu'au
 * rechargement, transition fluide. Fermer d'emblée créait un clignotement close/blanc/reopen.
 */

const STORAGE_KEY = 'monday:optin:v1';
const PENDING_KEY = 'monday:optin:pending';
const REARM_MS = 3000;

function readState() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
  } catch {
    // Safari en navigation privée, ou stockage refusé : on dégrade sans casser.
    return {};
  }
}

function writeState(patch) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...readState(), ...patch }));
  } catch {
    /* sans stockage, le popup se réaffichera : acceptable, jamais bloquant */
  }
}

class PopupOptin extends HTMLElement {
  #timer = null;
  #captchaTimer = null;
  // Passe à false pour les fermetures TECHNIQUES (submit), qui ne sont pas des refus.
  #dismissible = true;

  get #designMode() {
    return Boolean(window.Shopify && window.Shopify.designMode);
  }

  async connectedCallback() {
    // dialog-component (popup.js) est chargé en module : on attend sa définition
    // plutôt que de parier sur l'ordre d'exécution des scripts.
    await customElements.whenDefined('dialog-component');

    this.dialog = this.querySelector('dialog-component');
    if (!this.dialog) return;

    this.#unbindResizeClose();

    // Éditeur de thème : piloté par la sélection de section, jamais d'état persisté.
    if (this.#designMode) {
      this.#bindDesignMode();
      this.#setupNavigation();
      return;
    }

    // Une fermeture volontaire vaut refus : on horodate pour le plafond de fréquence.
    // Posé AVANT les early-returns pour couvrir aussi l'écran d'erreur.
    // Le `close` natif du <dialog> ne bulle pas : capture obligatoire pour le capter depuis
    // <dialog-component>, son ancêtre. Ne pas « nettoyer » ce capture.
    this.dialog.addEventListener(
      'close',
      () => {
        if (this.#dismissible) writeState({ dismissedAt: Date.now() });
      },
      { capture: true }
    );

    this.#watchSubmit();
    this.#setupNavigation();

    const success = this.querySelector('[data-optin-screen="3"]');
    const error = this.querySelector('[data-optin-error]');

    // form.posted_successfully? et form.errors sont GLOBAUX à la page : ils valent pour le TYPE de
    // formulaire ('customer'), pas pour l'instance. On ne réagit qu'à une soumission dont on est
    // l'auteur (flag posé par l'observateur passif du submit, repli sur le comptage des formulaires).
    const mine = this.#consumePending();

    // Retour d'une soumission native : on rouvre sur le bon écran, sans condition de fréquence.
    if (success) {
      if (!this.#isOurs(mine)) return;
      writeState({ subscribed: true });
      this.#open();
      this.#cleanUrl();
      return;
    }

    if (error) {
      if (!this.#isOurs(mine)) return;
      this.#open();
      return;
    }

    if (this.#shouldSkip()) return;

    const delay = Number(this.dataset.delay || 0) * 1000;
    this.#timer = setTimeout(() => this.#open(), delay);
  }

  disconnectedCallback() {
    if (this.#timer) clearTimeout(this.#timer);
    if (this.#captchaTimer) clearInterval(this.#captchaTimer);
  }

  /**
   * popup.js (#onResize) ferme la modale à TOUT changement de window.innerWidth : rotation d'écran,
   * redimensionnement, ancrage de fenêtre. Cette fermeture technique émet `close`, que le listener
   * ci-dessus compterait comme un refus (14 jours de silence, saisie perdue). Notre modale est
   * fluide, elle n'a rien à fermer sur resize : on retire le handler pour CETTE instance seulement.
   * popup.js reste intact pour le panier, la recherche et les volets.
   */
  #unbindResizeClose() {
    if (!this.dialog.resizeHandler) return;
    window.removeEventListener('resize', this.dialog.resizeHandler);
    this.dialog.resizeHandler = null;
  }

  #bindDesignMode() {
    const section = this.closest('.shopify-section');
    const id = section ? section.id.replace('shopify-section-', '') : null;
    if (!id) return;

    document.addEventListener('shopify:section:select', (event) => {
      if (event.detail.sectionId === id) this.dialog.open();
    });
    document.addEventListener('shopify:section:deselect', (event) => {
      if (event.detail.sectionId === id) this.dialog.close();
    });
  }

  /**
   * Navigation écran 1 -> écran 2, uniquement quand l'étape téléphone est active.
   * Le bouton « suivant » est rendu en type="button" côté Liquid : il ne soumet donc pas.
   * On valide l'email et le consentement AVANT de révéler l'écran 2 : ainsi ces deux champs
   * requis, une fois l'écran 1 caché, sont toujours valides au submit (une case `required` cachée
   * mais cochée reste valide ; cachée ET vide jetterait « invalid form control not focusable »).
   */
  #setupNavigation() {
    if (this.dataset.phoneStep !== 'true') return;

    const next = this.querySelector('[data-optin-next]');
    const screen1 = this.querySelector('[data-optin-screen="1"]');
    const screen2 = this.querySelector('[data-optin-screen="2"]');
    if (!next || !screen1 || !screen2) return;

    const goNext = () => {
      const email = this.querySelector('[data-optin-email]');
      const consent = this.querySelector('[data-optin-consent]');

      if (email && !email.checkValidity()) {
        email.reportValidity();
        return;
      }
      if (consent && !consent.checked) {
        consent.reportValidity();
        return;
      }

      screen1.hidden = true;
      screen2.hidden = false;

      // Focus sur le titre (tabindex -1), pas sur le champ téléphone : annonce le nouvel écran
      // aux lecteurs, sans ouvrir le clavier. L'utilisateur tape le numéro quand il le décide.
      const title = screen2.querySelector('[data-optin-screen2-title]');
      if (title) title.focus();
    };

    next.addEventListener('click', (event) => {
      event.preventDefault();
      goNext();
    });

    // Entrée dans un champ de l'écran 1 déclencherait le premier submit du form (un bouton de
    // l'écran 2, présent mais caché) et sauterait l'étape. On intercepte pour naviguer à la place.
    // preventDefault porte sur le keydown, jamais sur le submit : la règle native reste tenue.
    screen1.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      if (event.target && event.target.tagName === 'BUTTON') return;
      event.preventDefault();
      goNext();
    });
  }

  #watchSubmit() {
    const form = this.querySelector('.popup-optin__form');
    if (!form) return;

    form.addEventListener('submit', () => {
      // AUCUN preventDefault, AUCUN fetch : on observe, on n'intercepte jamais la soumission.
      // Les modifications de valeurs faites ici sont serialisées avec le reste (l'event submit
      // précède la sérialisation), y compris pour le form.submit() différé de hCaptcha.
      this.#applyWhatsAppConsent();

      // sessionStorage survit à la redirection pleine page et meurt avec l'onglet.
      try {
        sessionStorage.setItem(PENDING_KEY, '1');
      } catch {
        /* stockage refusé : #isOurs retombe sur le comptage DOM */
      }

      // Fermeture technique à venir possible (si défi) : ne pas la compter comme un refus.
      this.#dismissible = false;
      this.#markSubmitting();
      this.#watchForChallenge();
    });
  }

  /** Indice visuel de traitement + garde anti double-soumission, le temps du rechargement. */
  #markSubmitting() {
    this.querySelectorAll('button[type="submit"]').forEach((btn) => {
      btn.setAttribute('aria-busy', 'true');
      btn.disabled = true;
    });
  }

  /**
   * On NE ferme PAS le dialog d'emblée : dans le cas courant (pas de défi), la page navigue en
   * ~1s et le popup reste visible jusqu'au rechargement, ce qui donne une transition fluide au
   * lieu d'un clignotement close/blanc/reopen. On ne ferme QUE si hCaptcha escalade en défi
   * VISIBLE (iframe montée au niveau du <body>, ~520x570, inerte sous le dialog modal) : on la
   * détecte par sa taille et on libère alors le top layer. Le poll meurt avec la page à la
   * navigation ; sinon on l'arrête après quelques secondes.
   */
  #watchForChallenge() {
    let elapsed = 0;
    this.#captchaTimer = setInterval(() => {
      if (this.#captchaVisible()) {
        this.#closeNow();
        clearInterval(this.#captchaTimer);
        this.#captchaTimer = null;
        return;
      }
      elapsed += 120;
      if (elapsed > 6000) {
        clearInterval(this.#captchaTimer);
        this.#captchaTimer = null;
      }
    }, 120);
  }

  /**
   * Consentement WhatsApp coché => on pose le tag de consentement ET le numéro, dans les TAGS.
   *
   * Pourquoi le numéro passe par un tag et pas par contact[phone] : `form_type=customer` ne traite
   * PAS contact[phone]. Shopify ne reconnaît officiellement que contact[email] sur ce type de
   * formulaire (la doc du `form` object réserve `phone` à customer_address). Le champ part dans le
   * POST et se perd en silence, sans erreur. Le seul véhicule natif prouvé est contact[tags] : le
   * thème officiel Dawn s'en sert, et on a vérifié le 2026-07-16 sur une vraie fiche client que les
   * virgules créent bien des tags SÉPARÉS.
   *
   * Format du tag : les tags Shopify n'acceptent que lettres, chiffres et tirets (les caractères
   * spéciaux sont « ignorés ou traités comme identiques »), donc PAS de `wa:`. On pose
   * `wa-33612345678` = E.164 sans le +, préfixe en tiret.
   *
   * ⚠️ Le numéro dans un tag reste de la donnée personnelle dans un champ qui n'est pas prévu pour :
   * à consommer puis EFFACER (le service WhatsApp le reprendra via webhook customers/create, posera
   * le vrai champ phone + le consentement via l'Admin API, puis retirera le tag). Aucun consentement
   * ni numéro n'est posé sans la case cochée.
   */
  #applyWhatsAppConsent() {
    const consent = this.querySelector('[data-optin-wa-consent]');
    const phoneInput = this.querySelector('[data-optin-phone]');
    const tagsInput = this.querySelector('[data-optin-tags]');

    // Case non cochée = AUCUNE base légale : le numéro ne doit pas partir, même s'il a été saisi.
    // Cas réel : l'utilisateur tape son numéro, hésite, ne coche pas, puis clique « Passer cette
    // étape »  qui est lui aussi un submit. Sans ce garde, le numéro partait quand même.
    // Un champ `disabled` n'est pas sérialisé, et l'event submit précède la sérialisation.
    // Placé AVANT la garde !tagsInput, sinon il ne s'exécuterait pas.
    if (!consent || !consent.checked) {
      if (phoneInput) {
        phoneInput.value = '';
        phoneInput.disabled = true;
      }
      return;
    }

    if (!tagsInput) return;

    const tags = tagsInput.value
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    if (!tags.includes('optin-whatsapp')) tags.push('optin-whatsapp');

    const e164 = this.#normalizePhone(phoneInput ? phoneInput.value : '');
    if (e164) {
      const phoneTag = `wa-${e164}`;
      if (!tags.includes(phoneTag)) tags.push(phoneTag);
      // Laissé au format international au cas où Shopify traiterait contact[phone] un jour.
      // Sans effet aujourd'hui, mais jamais nuisible.
      if (phoneInput) phoneInput.value = `+${e164}`;
    }

    tagsInput.value = tags.join(',');
  }

  /**
   * Normalise un numéro saisi en E.164 SANS le + (chiffres seuls), marché FR par défaut :
   * 06 12 34 56 78 -> 33612345678, 0033... et +33... -> 33...
   * Renvoie '' si le résultat n'est pas plausible : on préfère ne rien poser qu'un tag faux.
   */
  #normalizePhone(raw) {
    const digits = String(raw || '').replace(/\D/g, '');
    if (!digits) return '';

    let national = digits;
    if (national.startsWith('00')) national = national.slice(2);
    else if (national.startsWith('0')) national = `33${national.slice(1)}`;

    if (national.length < 8 || national.length > 15) return '';
    return national;
  }

  /**
   * dialog-component.close() passe par #animateClose : 250 ms pendant lesquelles le dialog reste
   * modal, donc le défi reste inerte. On ferme sec, sans animation.
   */
  #closeNow() {
    const modal = this.dialog.dialogModal || this.querySelector('[data-ref="popup-modal"]');
    if (modal && modal.open) modal.close();
  }

  #consumePending() {
    // One-shot : le flag ne doit pas survivre à la navigation suivante (cas d'un défi hCaptcha
    // visible abandonné en cours de route).
    try {
      const pending = sessionStorage.getItem(PENDING_KEY) === '1';
      sessionStorage.removeItem(PENDING_KEY);
      return pending;
    } catch {
      return false;
    }
  }

  #isOurs(pending) {
    if (pending) return true;
    // Pas de flag : soit un autre form customer a posté, soit le stockage est refusé.
    // {% form 'customer' %} rend toujours <input name="form_type" value="customer">.
    // S'il n'y en a qu'un sur la page, c'est forcément le nôtre.
    return document.querySelectorAll('input[name="form_type"][value="customer"]').length <= 1;
  }

  /**
   * L'écran 3 est rendu par ?customer_posted=true, qui reste dans la barre d'adresse : un
   * rechargement ou un retour arrière rejoueraient le popup. On retire le seul paramètre concerné,
   * jamais toute la query (?variant= et les utm_* doivent survivre). La soumission est terminée.
   */
  #cleanUrl() {
    try {
      const url = new URL(location.href);
      if (!url.searchParams.has('customer_posted')) return;
      url.searchParams.delete('customer_posted');
      history.replaceState({}, '', url.pathname + url.search + url.hash);
    } catch {
      /* URL exotique : sans conséquence, on laisse le paramètre */
    }
  }

  #shouldSkip() {
    const state = readState();
    if (state.subscribed) return true;

    if (state.dismissedAt) {
      const days = Number(this.dataset.frequencyDays || 14);
      const elapsed = Date.now() - Number(state.dismissedAt);
      if (elapsed < days * 24 * 60 * 60 * 1000) return true;
    }

    return false;
  }

  #open() {
    // Ne JAMAIS s'ouvrir par-dessus une verification hCaptcha visible. Cas vecu le 2026-07-16 :
    // quand hCaptcha escalade, Shopify sert une page « Pour continuer, faites-nous savoir que vous
    // n'etes pas un robot ». Le popup vit dans le header group, donc il est rendu sur CETTE page
    // aussi : il se rouvrait par-dessus, sur l'ecran 1, et masquait la case a cocher que le
    // visiteur doit justement valider. Il croyait que sa soumission avait echoue.
    // On re-arme sans horodater : ce n'est ni un refus, ni une impression perdue.
    if (this.#captchaVisible() || document.body.classList.contains('overflow-hidden')) {
      // Le verrou body.overflow-hidden est le seul que posent TOUS les overlays du theme (panier,
      // burger, Sommaire, wishlist, zoom, stories, side-menu) : aucun n'est un <dialog>, donc
      // tester dialog[open] ne les verrait pas.
      this.#timer = setTimeout(() => this.#open(), REARM_MS);
      return;
    }
    this.dialog.open();
  }

  /** Une iframe hCaptcha de taille reelle = defi ou case « Je suis un humain » a l'ecran. */
  #captchaVisible() {
    return [...document.querySelectorAll('iframe')].some(
      (frame) => /hcaptcha/i.test(frame.src || '') && frame.getBoundingClientRect().width > 100
    );
  }
}

if (!customElements.get('popup-optin')) {
  customElements.define('popup-optin', PopupOptin);
}
